import { isValidElement, useMemo, useState } from 'react';
import TournamentLeaderboardTable from './TournamentLeaderboardTable';

const isUnsafeParticipantLabel = (value) => /^(player|temp):/i.test(String(value || '').trim());

const resolveDisplayName = (row = {}) => {
  const candidates = [row.name, row.displayName, row.playerName, row.player?.name, row.player?.displayName];
  const safeName = candidates
    .map((name) => String(name || '').trim())
    .find((name) => name && !isUnsafeParticipantLabel(name));
  return safeName || 'Unknown Player';
};

const withSafeDisplayNames = (rows = []) => rows.map((row) => ({
  ...row,
  name: resolveDisplayName(row),
  playerName: resolveDisplayName(row),
  displayName: resolveDisplayName(row),
}));

const TournamentPerformanceTabs = ({ performanceLeaders, leaders, teamLeaderboard }) => {
  const canonicalLeaders = useMemo(() => leaders || {}, [leaders]);
  const tabs = useMemo(
    () => {
      const leaderRows = (key, performanceKey = key) =>
        withSafeDisplayNames(canonicalLeaders?.[key] || performanceLeaders?.[performanceKey] || []);

      return [
      {
        key: 'teams',
        label: 'Teams',
        description: 'Team standings based on completed matches.',
        render: () => <TournamentLeaderboardTable leaderboard={teamLeaderboard} />,
      },
      {
        key: 'mvp',
        label: 'MVP',
        description: 'Overall impact rating across batting, bowling, and fielding.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Points', key: 'rating' },
          { label: 'Runs', key: 'runs' },
          { label: 'Wickets', key: 'wickets' },
          { label: 'Dismissals', key: 'dismissals' },
        ],
        rows: leaderRows('mvp').map((entry) => ({
          ...entry,
          dismissals: (entry.fieldingDismissals || 0) + (entry.wicketKeeperDismissals || 0),
        })),
      },
      {
        key: 'batter',
        label: 'Batter',
        description: 'Most runs with strike rate as the tie breaker.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Runs', key: 'runs' },
          { label: 'Balls', key: 'balls' },
          { label: 'Highest score', key: 'highestScore' },
          { label: 'Average', key: 'average' },
          { label: 'Strike rate', key: 'strikeRate' },
          { label: 'Fours', key: 'fours' },
          { label: 'Sixes', key: 'sixes' },
        ],
        rows: leaderRows('batting', 'batter'),
      },
      {
        key: 'bowler',
        label: 'Bowler',
        description: 'Most wickets with economy as the tie breaker.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Wickets', key: 'wickets' },
          { label: 'Overs', key: 'overs' },
          { label: 'Best figures', key: 'bestBowlingFigures' },
          { label: 'Economy', key: 'economy' },
          { label: 'Average', key: 'average' },
          { label: 'Strike rate', key: 'strikeRate' },
        ],
        rows: leaderRows('bowling', 'bowler'),
      },
      {
        key: 'fielder',
        label: 'Fielder',
        description: 'Most fielding or wicket-keeping dismissals.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Dismissals', key: 'dismissals' },
        ],
        rows: leaderRows('fielding', 'fielder'),
      },
      {
        key: 'wicketKeeper',
        label: 'Wicket keeper',
        description: 'Most wicket-keeping dismissals.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Dismissals', key: 'dismissals' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.wicketKeeper || []),
      },
      {
        key: 'mostCatches',
        label: 'Most catches',
        description: 'Highest number of catches.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Catches', key: 'catches' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostCatches || []),
      },
      {
        key: 'mostStumpings',
        label: 'Most stumpings',
        description: 'Highest number of stumpings.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Stumpings', key: 'stumpings' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostStumpings || []),
      },
      {
        key: 'mostRunOuts',
        label: 'Most run outs',
        description: 'Highest number of run outs.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Run outs', key: 'runOuts' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostRunOuts || []),
      },
      {
        key: 'mostSixes',
        label: 'Most sixes',
        description: 'Highest number of sixes.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Sixes', key: 'sixes' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostSixes || []),
      },
      {
        key: 'mostFours',
        label: 'Most fours',
        description: 'Highest number of fours.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Fours', key: 'fours' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostFours || []),
      },
      {
        key: 'mostBoundaries',
        label: 'Most boundaries',
        description: 'Highest number of boundaries.',
        columns: [
          { label: 'Player', key: 'name' },
          { label: 'Boundaries', key: 'boundaries' },
        ],
        rows: withSafeDisplayNames(performanceLeaders?.mostBoundaries || []),
      },
      ];
    },
    [canonicalLeaders, performanceLeaders, teamLeaderboard]
  );

  const [activeKey, setActiveKey] = useState(tabs[0]?.key || 'mvp');
  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0];
  const rows = activeTab?.rows || [];
  const renderCellValue = (value) => {
    if (isValidElement(value)) {
      return value;
    }

    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'object') {
      return value.name ?? value.label ?? '-';
    }

    return value;
  };

  return (
    <div className="card border-0 shadow-sm mb-4 leaderboard-insights">
      <div className="card-body">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <div>
            <h2 className="h6 text-uppercase text-muted mb-1">Leaderboard insights</h2>
            <p className="text-muted small mb-0">
              Team standings and top performers across completed matches.
            </p>
          </div>
        </div>

        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((tab) => (
            <li className="nav-item" role="presentation" key={tab.key}>
              <button
                className={`nav-link ${tab.key === activeKey ? 'active' : ''}`}
                type="button"
                role="tab"
                aria-selected={tab.key === activeKey}
                onClick={() => setActiveKey(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="border border-top-0 rounded-bottom p-3">
          <p className="text-muted small mb-3">{activeTab?.description}</p>
          {activeTab?.render
            ? activeTab.render()
            : rows.length
              ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Rank</th>
                          {activeTab.columns.map((column) => (
                            <th scope="col" key={column.key}>
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={`${activeTab.key}-${row.name}-${index}`}>
                            <td className="number-decor"></td>
                            {activeTab.columns.map((column) => (
                              <td key={column.key}>{renderCellValue(column.key === 'name' ? resolveDisplayName(row) : row[column.key])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              : <p className="text-muted small mb-0">No performance leaders yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default TournamentPerformanceTabs;
